import type { PropsWithChildren } from 'react';
import { createContext, useMemo } from 'react';

import type { ProductSearchCriteria } from '@jane/bloom/viewModels/productSearch';
import type {
  FilterTypeKeysWithJaneGold,
  ProductSearchFiltersViewModel,
} from '@jane/bloom/viewModels/productSearchFilters';
import { buildFilterKeysToDisplay } from '@jane/bloom/viewModels/productSearchFilters';

type BloomFilterContext = {
  availableFilters?: ProductSearchFiltersViewModel;
  displayFilterKeys: FilterTypeKeysWithJaneGold[];
  searchCriteria?: ProductSearchCriteria;
  /**
   * Whether to offer the Jane Gold filter at all — a function of the store, the
   * shopper's region, and the page. It governs visibility only: once shown, the
   * facet count decides whether the filter is selectable.
   */
  shouldShowGold: boolean;
};

export const BloomFilterContext = createContext<BloomFilterContext>({
  displayFilterKeys: [],
  shouldShowGold: false,
});

export function BloomFilterProvider({
  availableFilters,
  children,
  displayFilterKeys = buildFilterKeysToDisplay(),
  searchCriteria,
  shouldShowGold = false,
}: PropsWithChildren<Partial<BloomFilterContext>>) {
  const contextValue = useMemo(
    () => ({
      availableFilters,
      displayFilterKeys,
      searchCriteria,
      shouldShowGold,
    }),
    [availableFilters, displayFilterKeys, searchCriteria, shouldShowGold]
  );

  return (
    <BloomFilterContext.Provider value={contextValue}>
      {children}
    </BloomFilterContext.Provider>
  );
}
